import { css, cx } from '@linaria/core'
import { type FC, type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { tmsApi } from '#api/tmsApi'
import { Button } from '#components/Button'
import { Dropdown } from '#components/Dropdown'
import { IconButton } from '#components/IconButton'
import { toastError } from '#features/ErrorHandling/toastError'
import { SystemInformationOverlay, SystemInformationOverlayElements } from '#features/SystemInformation/SystemInformationOverlay'
import { resetSessionState, signInRedirect, signOutSilent, useLiveSessionExpiry, useUserData } from '#features/User/userManager'
import UserIcon from '#icons/user-circle-48.svg'
import { fragments, style } from '#styles'
import { theme } from '#styles/themes'
import { formatDuration } from '#utils/formatDuration'
import { sharedTestIds } from '#utils/sharedTestIds'

interface UserPopupProps {
  className?: string
  showUserAccountOptions?: boolean
}

const dropdownClass = css`
  display: flex;
  align-items: center;
`
const popupStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
  background-color: ${style.theme.backgroundSurfacePrimaryStrong};
  color: ${style.theme.textDefault};
  outline: 0;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
`

const userPopupContentClass = css`
  min-width: 220px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  
  > hr {
    background:  ${theme.borderSurface};
    opacity: 0.5;
  }

  > a {
    display: flex;
    justify-content: stretch;
    flex-direction: column;
    text-decoration: none;

  }
  
  > button:not(.accent) {
    line-height: 2.15em;

    &:hover {
      color: ${style.theme.textDefault};
      background: ${style.theme.backgroundSurfaceStandardSubtle};
    }

  }
  
  button.accent {
    margin-top: 8px;  
  }

  > div, > ._import, > ._export {
    display: flex;
    flex: 1;
    justify-content: flex-start;
    gap: 4px;
  }

  > ._import, > ._export {
    ${fragments.textStyle.body.s.regular}
  }

  ._info {
    padding: 4px 0;
    
    &._name {
      padding-top: 8px;
    }
  }
`

const UserPopupContent: FC<{ children?: ReactNode; showUserAccountOptions?: boolean; signedOutPath?: string; onOpenSystemInfo?: () => void }> = ({
  children,
  showUserAccountOptions = true,
  signedOutPath = '/signed-out',
}) => {
  const { t } = useTranslation('shared')
  const user = useUserData()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const expiresIn = useLiveSessionExpiry(user)

  return (
    <div className={userPopupContentClass}>
      <SystemInformationOverlayElements />

      {children && (
        <>
          <hr />
          {children}
        </>
      )}

      {showUserAccountOptions && (
        <>
          <hr />

          {user ? (
            <>
              <div className="_info _name">
                <strong>{t('user.name.label', 'User Name')}:</strong>
                {user.profile.name} {user.profile.given_name} {user.profile.family_name}
              </div>

              {user.profile.email && (
                <div className="_info _email">
                  <strong>{t('user.email.label', 'User Email')}:</strong>
                  {user.profile.email}
                </div>
              )}
            </>
          ) : (
            <div className="_info _not-found">
              <strong>{t('user.not-found', 'No user found')}</strong>
            </div>
          )}

          {expiresIn !== undefined && (
            <div className="_info _token-remaining">
              <strong>{t('user.token.remaining.label', 'Session Time Remaining:')}</strong>
              {formatDuration(expiresIn * 1000, undefined, { relative: false })}
            </div>
          )}

          {user && (
            <Button
              onClick={() => {
                navigate('/signed-out')

                setTimeout(() => {
                  resetSessionState()
                  dispatch(tmsApi.util.resetApiState())

                  void signOutSilent().catch(toastError)
                }, 1000)

                navigate(signedOutPath)
              }}
            >
              {t('user.button.sign-out', 'Sign Out')}
            </Button>
          )}

          {!user && (
            <Button
              onClick={() => {
                void signInRedirect().catch(toastError)
              }}
            >
              {t('user.button.sign-in', 'Sign In')}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

export const UserDropdown: FC<
  UserPopupProps &
    (
      | { children?: ReactNode; isOpen?: undefined; setIsOpen?: undefined }
      | { children?: ReactNode; isOpen: boolean; setIsOpen: (value: boolean) => void }
    )
> = ({ className, showUserAccountOptions = true, children, isOpen: isOpenExternal, setIsOpen: setIsOpenExternal }) => {
  const [isOpenInternal, setIsOpenInternal] = useState(false)
  const [isSystemInfoOpen, setIsSystemInfoOpen] = useState(false)
  const isOpen = isOpenExternal ?? isOpenInternal
  const setIsOpen = setIsOpenExternal ?? setIsOpenInternal
  const { t } = useTranslation('shared')

  return (
    <>
      <Dropdown
        withoutArrow
        popupClassName={popupStyle}
        open={isOpen}
        onOpenChange={setIsOpen}
        dropdownContent={
          <UserPopupContent
            showUserAccountOptions={showUserAccountOptions}
            onOpenSystemInfo={() => {
              setIsOpen(false)
              setIsSystemInfoOpen(true)
            }}
          >
            {children}
          </UserPopupContent>
        }
        className={dropdownClass}
        offset={{ mainAxis: 7.5, crossAxis: -10 }}
        arrowOffset={3}
        data-testid={sharedTestIds.systemInfo.showUserDropdown}
      >
        <IconButton
          iconSize="m"
          title={t('user.button.show-dropdown', 'Show User Dropdown')}
          className={cx(className, 'icon-left')}
          style={{ opacity: 1 }}
          iconUrl={UserIcon}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
        />
      </Dropdown>
      <SystemInformationOverlay isOpen={isSystemInfoOpen} setIsOpen={setIsSystemInfoOpen} />
    </>
  )
}
